import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { IDatasetTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataset-type-metadata';

import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { ignoreNullValues, salesforceObjectMetadata, writeBehavior } from './salesforce-object-metadata';
import { maxConcurrentConnectionsMetadata, partitionOptionConst } from '../consts/metadata-consts';
import { GlobalVariables } from 'app/services/globals';
import { format } from 'app/shared/utils/string.util';
import { getConnectorV2Name, UpdateMethod } from 'shell-models-lib';
import { getAppendOption, getUpsertOption } from 'app/registries/consts/copy-job-const';

const commonTableSelectionSettings: Partial<ControlMetadata> = {
    required: true,
    refreshTableAfterInitialization: true,
    showPreviewButton: true,
    enableEditOnEmpty: false,
    parameterizable: true
};

export const salesforceV2ObjectTableSelectionSettings: ControlMetadata = {
    ...commonTableSelectionSettings,
    type: 'table-selection',
    bind: 'tableName',
    label: stringResources.TableNameDisplay_ObjectApiName,
    additionalProperties: {
        listType: 'Object'
    },
    tableNameAliasInQueryResponse: 'object'
}

export const salesforceV2ObjectMetadata: IDatasetTypeMetadata = {
    ...salesforceObjectMetadata,
    isTableRequired: true,
    disabled: false,
    type: adfModelTypeMap.salesforceV2.datasetType,
    notSupportPhysicalType: false,
    warningMessage: null,
    warningMessageHyperlink: null,
    tableNameDisplay: __IS_FABRIC_DPE__ ?
        stringResources.TableNameDisplay_ObjectApiName :
        `${stringResources.TableNameDisplay_ObjectApiName}/${stringResources.IR_SendLog_ResultDialog_ReportId}`,
    tableNamePlural: stringResources.TableNameDisplay_ObjectApiNames,
    queryDisplayName: stringResources.SOQLQueryLabel,
    queryTooltipLink: 'https://aka.ms/salesforce-soqlquery-limitation',
    displayName: getConnectorV2Name(stringResources.Salesforce_DisplayText, __IS_FABRIC_DPE__),
    datasetNamePrefix: adfModelTypeMap.salesforce.datasetType,
    supportLinkedServiceTypes: [adfModelTypeMap.salesforceV2.linkedServiceType],
    controls: [
        {
            type: 'radio-switch',
            label: stringResources.Dataset_WriterTableOption,
            bind: 'sourceOption',
            options: [
                {
                    label: stringResources.TableNameDisplay_ObjectApiName,
                    value: 'objectApiName'
                },
                {
                    label: stringResources.IR_SendLog_ResultDialog_ReportId,
                    value: 'reportId'
                }
            ],
            defaultValue: 'objectApiName',
            cases: [
                {
                    caseValue: 'objectApiName',
                    children: [
                        salesforceV2ObjectTableSelectionSettings
                    ]
                },
                {
                    caseValue: 'reportId',
                    children: [
                        {
                            ...commonTableSelectionSettings,
                            type: 'table-selection',
                            bind: 'tableName',
                            label: stringResources.IR_SendLog_ResultDialog_ReportId,
                            additionalProperties: {
                                listType: 'Report'
                            },
                            tableNameAliasInQueryResponse: 'report'
                        }
                    ]
                }
            ]
        },
    ],
    copySourceControls: [
        {
            type: 'parameterizable',
            child: {
                type: 'checkbox',
                bind: 'includeDeletedObjects',
                label: stringResources.includeDeletedObj,
                defaultValue: false,
                description: stringResources.includeDeletedObjDesc,
                labelPosition: 'left'
            },
            dynamicContentTextClass: 'input-spaced'
        },
        {
            type: 'parameterizable',
            child: {
                type: 'checkbox',
                bind: 'preserveScaleFromSchema',
                label: stringResources.preserveScaleFromSchema,
                defaultValue: false,
                description: stringResources.preserveScaleFromSchemaDesc,
                labelPosition: 'left'
            },
            dynamicContentTextClass: 'input-spaced'
        },
        {
            type: 'parameterizable',
            hide: `{ (!isSoqlQuery && owner.sourceDataset && owner.sourceDataset.sourceOption === 'reportId') }`,
            child: {
                type: 'radio',
                bind: 'partitionOption',
                defaultValue: partitionOptionConst.autoDetect,
                label: stringResources.ModelName_Partition_Option,
                description: stringResources.autoPartitionDesc,
                options: [
                    {
                        label: stringResources.None,
                        value: partitionOptionConst.none
                    },
                    {
                        label: stringResources.autoDetect,
                        value: partitionOptionConst.autoDetect
                    }
                ],
                labelPosition: 'left'
            },
            dynamicContentTextClass: 'input-spaced'
        },
        {
            type: 'parameterizable',
            hide: !GlobalVariables.pageSizeSf,
            child: {
                type: 'textbox-number',
                bind: 'pageSize',
                label: stringResources.PageSizeLabel,
                inputType: 'number',
                min: 1,
                dataType: 'integer',
                validators: [
                    {
                        type: 'number',
                        min: 1,
                        errorMessage: format(stringResources.Above0ErrorMessage, stringResources.PageSizeLabel)
                    }
                ]
            },
            dynamicContentTextClass: 'input-spaced'
        }
    ],
    copySinkControls: [
        writeBehavior,
        ignoreNullValues,
        {
            type: 'parameterizable',
            child: {
                type: 'textbox-number',
                bind: 'writeBatchSize',
                label: stringResources.ModelName_WriteBatchSize,
                defaultValue: 100000,
                inputType: 'number',
                min: 0
            },
            dynamicContentTextClass: 'input-spaced'
        },
        maxConcurrentConnectionsMetadata
    ],
    copyJob: {
        updateMethod: {
            supportUpdateMethod: true,
            options: [getAppendOption(), getUpsertOption()],
            updateMethodProperties: ['writeBehavior', 'externalIdFieldName'],
            defaultUpdateMethod: UpdateMethod.Append,
            useUpsertIdInput: true,
            upsertKeyColumnLabel: stringResources.ModelName_SalesforceExternalIdFieldName,
            upsertKeyColumnDescription: stringResources.SalesforceExternalIdFieldNameDescription,
            upsertKeyColumnRequired: true
        }
    }
};
